import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/animations/variants';

interface Logo {
  name: string;
  icon: string;
  href?: string;
}

interface LogoStripProps {
  title?: string;
  logos: Logo[];
  animated?: boolean;
  className?: string;
}

export default function LogoStrip({ 
  title, 
  logos, 
  animated = true, 
  className = '' 
}: LogoStripProps) {
  return (
    <motion.div
      className={`py-16 ${className}`}
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {title && (
        <motion.div 
          className="text-center mb-12"
          variants={fadeUp}
        >
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-textPrimary">
            {title}
          </h2>
        </motion.div>
      )}

      <motion.div 
        className="flex flex-wrap justify-center items-center gap-8 md:gap-12"
        variants={staggerContainer}
      >
        {logos.map((logo, index) => (
          <motion.div
            key={index}
            className="flex flex-col items-center group cursor-pointer"
            variants={fadeUp}
            whileHover={{ 
              scale: 1.05,
              y: -5,
            }}
            transition={{ duration: 0.2 }}
          >
            {logo.href ? (
              <a 
                href={logo.href}
                className="flex flex-col items-center text-textSecondary hover:text-textPrimary transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="text-4xl md:text-5xl mb-2 opacity-70 group-hover:opacity-100 transition-opacity">
                  {typeof logo.icon === 'string' && logo.icon.match(/\.(png|jpg|jpeg|svg|gif)$/i) ? (
                    <img src={logo.icon} alt={logo.name} className="w-16 h-16 object-contain" />
                  ) : (
                    logo.icon
                  )}
                </div>
                <span className="text-sm font-medium">
                  {logo.name}
                </span>
              </a>
            ) : (
              <div className="flex flex-col items-center text-textSecondary group-hover:text-textPrimary transition-colors">
                <div className="text-4xl md:text-5xl mb-2 opacity-70 group-hover:opacity-100 transition-opacity">
                  {typeof logo.icon === 'string' && logo.icon.match(/\.(png|jpg|jpeg|svg|gif)$/i) ? (
                    <img src={logo.icon} alt={logo.name} className="w-16 h-16 object-contain" />
                  ) : (
                    logo.icon
                  )}
                </div>
                <span className="text-sm font-medium">
                  {logo.name}
                </span>
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* Animated Moving Strip (Optional) */}
      {animated && (
        <motion.div 
          className="mt-16 overflow-hidden"
          variants={fadeUp}
        >
          <motion.div
            className="flex items-center justify-center gap-16"
            animate={{ x: [0, -100, 0] }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            {[...logos, ...logos].map((logo, index) => (
              <div
                key={index}
                className="flex-shrink-0 text-2xl opacity-30"
              >
                {typeof logo.icon === 'string' && logo.icon.match(/\.(png|jpg|jpeg|svg|gif)$/i) ? (
                  <img src={logo.icon} alt={logo.name} className="w-10 h-10 object-contain" />
                ) : (
                  logo.icon
                )}
              </div>
            ))}
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}